<template>
  <div class="offcanvas offcanvas-end" tabindex="-1" :id="offcanvas_id" data-bs-backdrop="static" :aria-labelledby="`${offcanvas_id}_label`">
    <div class="offcanvas-header">
      <button class="btn-back" :id="`btnClose${offcanvas_id}`" type="button" name="button" data-test="btnCloseOffcanvas" data-bs-dismiss="offcanvas" @click="doCancel" aria-label="Close">
        <IconArrowBack/>
        {{ $t('goBack') }}
      </button>
      <h5 class="offcanvas-title d-block" :id="`${offcanvas_id}_label`">
        {{ $t('patientSearch.searchingFor') }}
        <strong>{{ searchValue }}</strong>
      </h5>
    </div>

    <div class="offcanvas-body">
      <p>{{ $t('patientSearch.selectThePatient') }}</p>

      <div v-if="patientList.items?.length > 0" class="align-items-center pagination-row">
        <p class="pagination-total">{{ `${patientList.total} ${$t("pagination.patients")}` }}</p>
        <BasePagination :total-pages="totalPages" :total="patientList.total" :is-loading="loading"
                        :per-page="perPage" :current-page="currentPage" @pagechanged="onPageChange"/>
      </div>

      <div class="no-results-box" v-if="alert.message">
        <BaseNoResults :title="alert.title" :text="alert.message"/>
      </div>

      <ul class="scrollbar" v-if="patientList.items?.length > 0">
        <li v-for="(item, index) in patientList.items" :key="index" @click="doConfirm(item)">
          <span>{{ item.fullName }}</span>

          <small>{{ formatDate(item.lastExamDate) }}</small>

          <div class="id-box">
            <div class="icon-sub"></div>
            <span>{{ $utils.getFormattedPatientDocumentWithSearchMatch(item, searchValue, $i18n) }}</span>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.offcanvas {
  .offcanvas-title {
    font-size:                          var(--font-size-22);

    strong {
      font-size:                        38px;
      display:                          block;
      font-weight:                      700;
      text-transform:                   capitalize;
    }
  }

  .offcanvas-body {
    display:                            flex;
    flex-direction:                     column;
    justify-content:                    flex-start;
    gap:                                26px;

    p {
      color:                            var(--Brand-Primary);
      font-size:                        var(--font-size-14);
      font-weight:                      300;
      line-height:                      140%; /* 19.6px */
      margin-bottom:                    0;
    }

    ul {
      display:                          block;
      max-height:                       calc(100vh - 200px);
      margin-bottom:                    0;
      padding:                          0 10px 0 0;

      li {
        @include                        border_radius(10px);
        display:                        flex;
        flex-wrap:                      wrap;
        justify-content:                space-between;
        align-items:                    center;
        // gap:                            10px;
        margin-bottom:                  10px;
        padding:                        10px;
        background:                     var(--Shades-10);
        border:                         1px solid var(--Shades-20);
        cursor:                         pointer;

        &:last-child {
          margin:                       0;
        }

        span {
          color:                        var(--Brand-Primary);
          font-size:                    var(--font-size-16);
          font-weight:                  400;
          line-height:                  normal;
          text-overflow:                ellipsis;
          overflow:                     hidden;
        }

        small {
          color:                        var(--Shades-70);
          font-size:                    var(--font-size-10);
          font-weight:                  700;
          line-height:                  normal;
          letter-spacing:               0.5px;
        }

        .id-box {
          display:                      flex;
          gap:                          5px;
          width:                        100%;
          margin:                       5px 0 0 5px;

          .icon-sub {
            @include                    border_radius(1px);
            width:                      8px;
            height:                     8px;
            border-left:                1.5px solid var(--Shades-30);
            border-bottom:              1.5px solid var(--Shades-30);
          }

          span {
            color:                      var(--Shades-70);
            font-size:                  var(--font-size-12);
            font-weight:                400;
            line-height:                normal;
            letter-spacing:             0.6px;
          }
        }
      }
    }
  }
}

:deep(.no-results-main) {
  text-align:                          center;
}

.pagination-row {
  margin-bottom:                           26px;
}

.pagination-total {
  margin-bottom:                           0 !important;
}

:deep(.pagination.justify-content-end) {
  flex-direction:                          row;

  .disabled:not(.page-item) {
    display:                               flex;
    align-items:                           center;
    gap:                                   5px;

    span {
      white-space:                         nowrap;
    }
  }
}
</style>

<script>
import { useUserStore } from '@/store/user'

export default {
  name: 'ExamListSearchedPatientsOffcanvas',
  props: {
    offcanvas_id: String
  },
  data: () => ({
    loading: false,
    patientList: {
      items: [],
      total: 0
    },
    perPage: 20,
    currentPage: 1,
    totalPages: 0,
    searchValue: null,
    selectedPatient: {},
    offcanvas_list_searched_patients: null,
    resolvePromise: null,
    rejectPromise: null,
    session: {},
    alert: {
      title: null,
      message: null,
      class: null
    },
  }),
  mounted() {
    const userStore = useUserStore()
    this.session = userStore.sessionInfo

    const offcanvasElement = document.getElementById(this.offcanvas_id)

    if (offcanvasElement) {
      this.offcanvas_list_searched_patients = new bootstrap.Offcanvas(offcanvasElement)
      offcanvasElement.addEventListener('hidden.bs.offcanvas', this.handleHiddenEvent)
    }
  },
  methods: {
    show(options = {}) {
      if (options.patientName)
        this.searchValue = options.patientName

      this.currentPage = 1
      this.fetchData();

      this.$nextTick(() => {
        if (this.offcanvas_list_searched_patients) {
          this.offcanvas_list_searched_patients.show()
        } else {
          const offcanvasElement = document.getElementById(this.offcanvas_id)
          this.offcanvas_list_searched_patients = new bootstrap.Offcanvas(offcanvasElement)
          this.offcanvas_list_searched_patients.show()
          offcanvasElement.addEventListener('hidden.bs.offcanvas', this.handleHiddenEvent)
        }
      });

      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve;
        this.rejectPromise = reject;
      });
    },

    doConfirm(patient) {
      if (this.offcanvas_list_searched_patients) {
        this.offcanvas_list_searched_patients.hide()
      }

      this.resolvePromise(patient)
    },

    doCancel() {
      if (this.offcanvas_list_searched_patients) {
        this.offcanvas_list_searched_patients.hide()
      }

      this.resolvePromise(false)
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchData();
    },

    async fetchData() {
      this.alert = {}

      const params = {
        page: this.currentPage,
        q: this.searchValue,
        researchFlag: this.session.features.includes('researchFlag')
      }

      this.loading = true
      NProgress.start()

      try {
        const response = await this.$patientService.getList(params);

        this.patientList = {
          items: response.result,
          total: response.totalCount
        }

        this.totalPages = Math.ceil(response.totalCount / this.perPage);

        if (response.result.length === 0) {
          this.patientList = { items: [], total: 0 }
          this.totalPages = 1
          this.alert = this.$message.alertWithTitle(this, 'request_notfound_title', 'request_notfound_text')
        }
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error');
        this.patientList = { items: [], total: 0 }
      } finally {
        NProgress.done(true);
        this.loading = false;
      }
    },

    formatDate(date) {
      if (!date)
        return ''

      return moment(date).format(this.$i18n.t('dateTimeFormat'))
    },

    handleHiddenEvent() {
      if (this.resolvePromise && this.offcanvas_list_searched_patients._isShown) {
        this.resolvePromise(false)
      } else if (this.resolvePromise) {
        this.resolvePromise(false)
      }

      this.resetState()
      this.offcanvas_list_searched_patients = null
    },

    resetState() {
      this.patientList = { items: [], total: 0 }
      this.currentPage = 1
      this.totalPages = 0
      this.searchValue = null
      this.selectedPatient = {}
    },
  },
  beforeUnmount() {
    const offcanvasElement = document.getElementById(this.offcanvas_id)
    if (offcanvasElement) {
      offcanvasElement.removeEventListener('hidden.bs.offcanvas', this.handleHiddenEvent)
    }
  },
}
</script>
